import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import type { ActionButton } from '@app/src/components/ActionButtons'
import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import DrawerViewExport from '@app/src/components/Drawer/Views/DrawerViewExport'
import { useDrawer_DEPRECATED } from '@app/src/components/Drawer_DEPRECATED'
import EmptyState from '@app/src/components/EmptyState'
import HasWriteAccess from '@app/src/components/HasWriteAccess'
import LinkButton from '@app/src/components/LinkButton'
import UpdateProvidersDrawer from '@app/src/components/ManageProviderDrawer/UpdateProvidersDrawer'
import Table from '@app/src/components/Table'
import { useRiskSettings } from '@app/src/context/RiskSettingsContext'
import usePaginationWithQuery from '@app/src/hooks/paginationWithQuery'
import { useGetApiQueryFilters } from '@app/src/hooks/queryFilters'
import useSort from '@app/src/hooks/sorting'
import { useBulkCreateExpectation } from '@app/src/hooks/useBulkCreateExpectation'
import { useWriteAccess } from '@app/src/hooks/useWriteAccess'
import { CONFIGURATION_PAGE_IDS } from '@app/src/pages/Configurations/ConfigurationsScene'
import RiskScreeningHqFilters from '@app/src/pages/ResourceCollection/Filters/RiskScreeningHqFilters'
import type { ResourceCollectionSceneProps } from '@app/src/pages/ResourceCollection/ResourceCollectionScene'
import ResourceCollectionScene from '@app/src/pages/ResourceCollection/ResourceCollectionScene'
import type { Provider, RiskTableView } from '@app/src/types/organizations'
import { insertIf } from '@app/src/utils/helpersTs'
import { ResourceTypes } from '@app/src/wf-constants'
import paths from '@app/src/wf-constants/paths'
import { LabelOutlined, PublishedWithChangesOutlined, TuneOutlined } from '@mui/icons-material'
import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { generatePath } from 'react-router'
import RiskScreeningHqHeader from './RiskScreeningHqHeader'
import RiskScreeningHqRow from './RiskScreeningHqRow'
import { RiskScreeningViews } from './RiskScreeningScene'

interface RiskScreeningHqSceneProps {
  tabs?: ResourceCollectionSceneProps['tabs']
  actionButtons?: ResourceCollectionSceneProps['actionButtons']
  subtitle?: React.ReactNode
}

const allowedFilters: string[] = [
  'id',
  'name',
  'country.id',
  'createdAt',
  'providerOwners.userId',
  'providerOwners.user',
  'websiteAddress',
  'vatNumber',
  'registrationNumber',
  'linkStatus',
  'categoryOptions.id',
  'activityStatus',
  'finalRiskRating',
  'priority',
  'providerApprovalStatus',
  'supplierUsage',
  'tier',
  'organization.id',
  'mappingNodes.actorType.id',
  'mappingNodes.tier',
  'riskStatus',
  'spends.periodName',
  'spends.spendClassification',
  'assessments.periodName',
  'assessments.totalLevel',
  'assessments.section1Level',
  'assessments.section2Level',
  'assessments.section3Level',
  'assessments.section4Level',
  'assessments.section5Level',
  'assessments.assessmentTemplate.name',
  'assessments.assessmentTemplate.id',
  'assessments.assessmentTemplate.AssessmentType',
]

const RiskScreeningHqScene: React.FC<RiskScreeningHqSceneProps> = ({ tabs, actionButtons, subtitle }): JSX.Element => {
  const filters = useGetApiQueryFilters(allowedFilters)
  const [pageNumber, itemsPerPage, setPage, setPageSize] = usePaginationWithQuery(undefined, [filters])
  const { sorting, toggleSorting } = useSort()
  const { formatMessage } = useIntl()
  const { openDrawer } = useDrawer()
  const { activeCountryRiskSettings, isLoadingRiskSettings } = useRiskSettings()
  const hasWriteAccess = useWriteAccess()

  const [selectedProviders, setSelectedProviders] = useState<Array<number>>([])
  const [isUpdateDrawerOpen, openUpdateDrawer, closeUpdateDrawer] = useDrawer_DEPRECATED(false)

  const payload = {
    filter: filters,
    pagination: {
      pageNumber,
      itemsPerPage,
    },
    sort: sorting,
    include: ['providerOwners.user', 'assessments'],
  }

  const rawExportPayload = {
    filter: [],
    pagination: {
      pageNumber,
      itemsPerPage,
    },
    sort: sorting,
    include: ['providerOwners.user'],
  }

  const { items, count, isLoading, isFetching, isError } = useFetchCollectionWithPost<RiskTableView>({
    key: FetchKey.RiskScreening,
    endpoint: endpoints.riskScreening,
    payload,
    options: {
      enabled: Boolean(activeCountryRiskSettings.length),
    },
  })

  const isFetchingProviders = isLoading || isFetching

  const handleExport = (selectedIds?: number[]) => {
    openDrawer(
      <DrawerViewExport
        resourceType={ResourceTypes.RiskScreening}
        resourceView={RiskScreeningViews.Headquarter}
        count={count}
        userFilter={filters}
        rawExportPayload={rawExportPayload}
        disableExportSettings
        selectedIds={selectedIds}
      />,
    )
  }

  const buttons: ActionButton[] = [
    ...(actionButtons ?? []),
    {
      label: formatMessage({ id: 'resourceCollections.general.export' }),
      variant: 'outlined',
      onClick: () => handleExport(selectedProviders),
      disabled: isLoading || count === 0,
    },
  ]

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>, provider: Provider) => {
    if (e.target.checked) {
      setSelectedProviders(prev => [...prev, provider.id])
    } else {
      setSelectedProviders(prev => prev.filter(providerId => providerId !== provider.id))
    }
  }

  const selectedProvidersForExpectation = useMemo(
    () =>
      items
        .filter(riskView => selectedProviders.includes(riskView.parentObject.id))
        .map(riskView => riskView.parentObject as Provider),
    [items, selectedProviders],
  )

  const {
    isEnabled: showBaselineExpectation,
    open: openBulkCreateExpectation,
    modal: bulkExpectationModal,
  } = useBulkCreateExpectation(selectedProvidersForExpectation)

  return (
    <ResourceCollectionScene
      title={formatMessage({ id: 'navbar.riskScreening' })}
      actionButtons={buttons}
      tabs={tabs}
      subtitle={subtitle}
      filter={<RiskScreeningHqFilters allowedFilters={allowedFilters} />}
      buttonRow={
        selectedProviders.length
          ? [
              ...insertIf<ActionButton>(hasWriteAccess, {
                label: formatMessage({ id: 'riskScreening.categorize' }, { count: selectedProviders.length }),
                startIcon: <LabelOutlined />,
                onClick: openUpdateDrawer,
                disabled: isFetchingProviders,
              }),
              ...insertIf<ActionButton>(hasWriteAccess && showBaselineExpectation, {
                label: formatMessage({ id: 'bulkCreateExpectation.entryButton' }, { count: selectedProviders.length }),
                startIcon: <PublishedWithChangesOutlined />,
                onClick: openBulkCreateExpectation,
                disabled: isFetchingProviders,
              }),
              {
                label: formatMessage(
                  { id: 'resourceCollections.general.exportWithCount' },
                  { count: selectedProviders.length },
                ),
                onClick: () => handleExport(selectedProviders),
                disabled: isLoading || count === 0,
              },
            ]
          : undefined
      }
    >
      <Table<RiskTableView>
        RowComponent={({ row }): JSX.Element => (
          <RiskScreeningHqRow
            row={row}
            enabledRiskTypes={activeCountryRiskSettings}
            onCheckboxChange={handleCheckboxChange}
            selectedProviders={selectedProviders}
            activeFilters={filters}
          />
        )}
        HeaderComponent={() => (
          <RiskScreeningHqHeader
            toggleSorting={toggleSorting}
            activeSorting={sorting}
            enabledRiskTypes={activeCountryRiskSettings}
            selectedProviders={selectedProviders}
            setSelectedProviders={setSelectedProviders}
            providersInPage={items.map(i => i.parentObject)}
          />
        )}
        data={items}
        page={pageNumber}
        pageSize={itemsPerPage}
        setPage={setPage}
        setPageSize={setPageSize}
        isLoading={isLoading || isFetching || isLoadingRiskSettings}
        count={count}
        isError={isError}
        emptyState={
          !activeCountryRiskSettings.length && (
            <EmptyState
              iconComponent={TuneOutlined}
              title={formatMessage({ id: 'dashboard.sourcing.countryRiskMap.emptyState.title' })}
              description={formatMessage({ id: 'dashboard.sourcing.countryRiskMap.emptyState.description' })}
            >
              <HasWriteAccess>
                <LinkButton
                  variant="outlined"
                  to={generatePath(paths.configurations, { configurationsPage: CONFIGURATION_PAGE_IDS.RiskScreening })}
                >
                  {formatMessage({ id: 'dashboard.sourcing.countryRiskMap.emptyState.action' })}
                </LinkButton>
              </HasWriteAccess>
            </EmptyState>
          )
        }
      />
      <UpdateProvidersDrawer
        selectedProviders={items
          .filter(riskView => selectedProviders.includes(riskView.parentObject.id))
          .map(riskView => riskView.parentObject)
          .map(provider => ({
            providerId: provider.id,
            name: provider.name,
            activityStatus: provider.activityStatus,
            finalRiskRating: provider.finalRiskRating,
            priority: provider.priority,
            providerApprovalStatus: provider.providerApprovalStatus,
            categoryOptionIds: provider.categoryOptions?.map(catOpt => catOpt.id),
            ownerUserIds: provider.ownerUserIds,
            tier: provider.tier,
            supplierUsage: provider.supplierUsage,
          }))}
        setSelectedProviders={setSelectedProviders}
        isDrawerOpen={isUpdateDrawerOpen}
        closeDrawer={closeUpdateDrawer}
      />
      {bulkExpectationModal}
    </ResourceCollectionScene>
  )
}

export default RiskScreeningHqScene
